import { useState, useEffect, useRef } from "react";
import {
  Receipt, CheckCircle2, Loader2, AlertCircle
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { BlockCard } from "../../../components/BlockCard";
import { API, getAuthHeaders, formatDate } from "./helpers";
import { toast } from "sonner";

export function FactureAMOSection({ dossierId, dossier, onUpdate }) {
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [docsExist, setDocsExist] = useState({ devis: false, contrat: false });
  const [factureDetails, setFactureDetails] = useState(null);
  const hasCalledUpdate = useRef(false);

  const invoice = dossier?.pennylane_invoice;
  const headers = getAuthHeaders(dossier?.tenant_id, "application/json");

  useEffect(() => {
    let cancelled = false;
    const checkDocs = async () => {
      try {
        const hdrs = getAuthHeaders(dossier?.tenant_id);
        await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/sync-tef-data`, {
          method: 'POST', headers: { ...hdrs, "Content-Type": "application/json" },
          credentials: 'include', body: JSON.stringify({})
        }).catch(() => {});
        const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/devis-contrats`, {
          headers: hdrs, credentials: 'include'
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          const docs = data.documents || [];
          setDocsExist({
            devis: docs.some(d => d.type_document === "devis_amo" && (d.pdf_genere || d.pdf_url || d.statut === "genere")),
            contrat: docs.some(d => d.type_document === "contrat_amo" && (d.pdf_genere || d.pdf_url || d.statut === "genere")),
          });
        }
      } catch (e) { console.error("FactureAMO check docs:", e); }
    };
    // Fetch facture details from metier_factures for HT/TTC
    const fetchFactureDetails = async () => {
      if (!invoice?.invoice_number) return;
      try {
        const hdrs = getAuthHeaders(dossier?.tenant_id);
        const res = await fetch(`${API}/api/metier/factures/by-number/${encodeURIComponent(invoice.invoice_number)}`, {
          headers: hdrs, credentials: 'include'
        });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setFactureDetails(data);
        }
      } catch (e) { console.error("FactureAMO fetch details:", e); }
    };
    if (dossierId) { checkDocs(); fetchFactureDetails(); }
    return () => { cancelled = true; };
  }, [dossierId, invoice?.invoice_number]);

  const tef = dossier?.tef_data || {};
  const hasDevisAMO = docsExist.devis || tef.devis_amo_genere === true;
  const hasContratAMO = docsExist.contrat || tef.contrat_amo_genere === true;
  const canGenerateFacture = hasDevisAMO && hasContratAMO;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/pennylane/create-invoice/${dossierId}`, {
        method: "POST", headers, body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Facture AMO générée et envoyée vers Pennylane");
        setShowForm(false);
        onUpdate?.();
      } else {
        toast.error(data.error || "Erreur génération facture");
      }
    } catch (e) { toast.error("Erreur réseau"); }
    finally { setGenerating(false); }
  };

  const handleMarkPaid = async () => {
    try {
      const res = await fetch(`${API}/api/pennylane/update-payment/${dossierId}`, {
        method: "PATCH", headers, body: JSON.stringify({ payment_status: "paid" })
      });
      if (res.ok) { toast.success("Facture marquée comme payée"); onUpdate?.(); }
      else toast.error("Erreur mise à jour paiement");
    } catch (e) { toast.error("Erreur"); }
  };

  const handleMarkUnpaid = async () => {
    try {
      const res = await fetch(`${API}/api/pennylane/update-payment/${dossierId}`, {
        method: "PATCH", headers, body: JSON.stringify({ payment_status: "pending" })
      });
      if (res.ok) { toast.success("Statut remis en attente"); onUpdate?.(); }
    } catch (e) { toast.error("Erreur"); }
  };

  // Refonte P0 : état de l'OBJET facture Pennylane lui-même (paid/partial/pending),
  // distinct du prédicat dossier helpers.js:isFacturePayee → volontairement NON unifié.
  const paymentStatus = invoice?.payment_status;
  const statusLabel = paymentStatus === "paid" ? "Payée" : paymentStatus === "partial" ? "Paiement partiel" : "En attente de paiement";
  const statusColor = paymentStatus === "paid" ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-[var(--oh-success-border)]" : paymentStatus === "partial" ? "bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] border-[var(--oh-accent)]" : "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border-[var(--oh-info-border)]";

  // ARB-12 — grille 4 champs du mockup-finance (toujours affichée) :
  // STATUT · RÉF FACTURE · MONTANT TTC · DATE RÈGLEMENT. Valeurs réelles si la
  // facture existe, placeholders sinon (Pennylane = source de vérité).
  const gridStatut = invoice ? statusLabel : "Non payé";
  const gridRef = invoice?.invoice_number || "—";
  const gridTTCval = factureDetails?.montant_ttc ?? invoice?.amount_ttc ?? invoice?.amount;
  const gridTTC = gridTTCval != null ? `${gridTTCval.toLocaleString('fr-FR')} €` : "—";
  const gridDateVal = invoice?.paid_at || dossier?.date_reglement;
  const gridDateReglement = gridDateVal ? formatDate(gridDateVal) : "—";

  // CC-DEV1-15 — migration <BlockCard> (cohérence des 4 onglets) : remplace le
  // thème slate sombre historique par les tokens var(--oh-*) (dual dark/light).
  return (
    <BlockCard
      icon={Receipt}
      iconColor="var(--oh-stat-amber-text)"
      title="Facture AMO — Pennylane"
      testId="facture-amo-section"
      badge={invoice ? (
        <Badge data-testid="facture-amo-status" className={statusColor}>{statusLabel}</Badge>
      ) : (
        <Badge className="border-0" style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)" }}>Non générée</Badge>
      )}
    >
      {/* ARB-12 — Grille 4 champs (mockup-finance) TOUJOURS affichée :
          STATUT · RÉF FACTURE · MONTANT TTC · DATE RÈGLEMENT */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="facture-amo-grid">
        <div className="rounded-lg p-3" style={{ background: "var(--oh-bg-surface)" }}>
          <div className="text-xs uppercase mb-1" style={{ color: "var(--oh-text-muted)" }}>Statut</div>
          <p className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>{gridStatut}</p>
        </div>
        <div className="rounded-lg p-3" style={{ background: "var(--oh-bg-surface)" }}>
          <div className="text-xs uppercase mb-1" style={{ color: "var(--oh-text-muted)" }}>Réf facture</div>
          <p className="text-sm font-mono" style={{ color: "var(--oh-text)" }}>{gridRef}</p>
        </div>
        <div className="rounded-lg p-3" style={{ background: "var(--oh-bg-surface)" }}>
          <div className="text-xs uppercase mb-1" style={{ color: "var(--oh-text-muted)" }}>Montant TTC</div>
          <p className="font-medium" style={{ color: "var(--oh-stat-amber-text)" }}>{gridTTC}</p>
        </div>
        <div className="rounded-lg p-3" style={{ background: "var(--oh-bg-surface)" }}>
          <div className="text-xs uppercase mb-1" style={{ color: "var(--oh-text-muted)" }}>Date règlement</div>
          <p className="text-sm" style={{ color: "var(--oh-text)" }}>{gridDateReglement}</p>
        </div>
      </div>

      {invoice ? (
        <div className="space-y-3">
          {invoice.paid_at && <p className="text-sm" style={{ color: "var(--oh-stat-green-text)" }}>Payée le {formatDate(invoice.paid_at)}</p>}
          <div className="flex gap-2 flex-wrap">
            {paymentStatus !== "paid" ? (
              <Button data-testid="mark-amo-paid-btn" size="sm" className="bg-green-600 hover:bg-green-700 text-[var(--oh-accent-contrast)]" onClick={handleMarkPaid}>
                <CheckCircle2 className="w-4 h-4 mr-1" /> Marquer payée
              </Button>
            ) : (
              <Button data-testid="mark-amo-unpaid-btn" size="sm" variant="outline" onClick={handleMarkUnpaid}>Remettre en attente</Button>
            )}
            {(invoice.file_url || factureDetails?.file_url) && (
              <Button size="sm" variant="outline" asChild>
                <a href={invoice.file_url || factureDetails.file_url} target="_blank" rel="noopener noreferrer">Voir sur Pennylane</a>
              </Button>
            )}
            {invoice.pennylane_id && (
              <Button size="sm" variant="outline" asChild data-testid="pennylane-link">
                <a href={`https://app.pennylane.com/companies/21936178/invoices/customer_invoices/${invoice.pennylane_id}`} target="_blank" rel="noopener noreferrer">
                  <Receipt className="w-3.5 h-3.5 mr-1.5" /> Ouvrir dans Pennylane
                </a>
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div>
          {!showForm ? (
            <div className="space-y-2">
              <Button
                data-testid="generate-amo-btn"
                className="bg-amber-600 hover:bg-amber-700 text-[var(--oh-accent-contrast)]"
                onClick={() => setShowForm(true)}
                disabled={!canGenerateFacture}
                title={!canGenerateFacture ? "Générez d'abord le Devis AMO et le Contrat AMO" : undefined}
              >
                <Receipt className="w-4 h-4 mr-2" /> Générer la facture (Pennylane)
              </Button>
              {!canGenerateFacture && (
                <p className="text-sm flex items-center gap-2" style={{ color: 'var(--oh-text-muted)' }}>
                  <AlertCircle className="w-4 h-4 text-[var(--oh-info-text)]" />
                  Générez d'abord le Devis AMO et le Contrat AMO
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3 rounded-lg p-4" style={{ background: "var(--oh-bg-surface)" }}>
              <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>La facture sera créée à partir du devis AMO du dossier et envoyée vers Pennylane.</p>
              <div className="flex gap-2">
                <Button data-testid="confirm-generate-amo-btn" className="bg-amber-600 hover:bg-amber-700 text-[var(--oh-accent-contrast)]" onClick={handleGenerate} disabled={generating}>
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />} Confirmer et envoyer vers Pennylane
                </Button>
                <Button variant="ghost" onClick={() => setShowForm(false)}>Annuler</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </BlockCard>
  );
}
